import { Link, useParams } from "react-router-dom";
import { Headphones, Heart, MessageCircle } from "lucide-react";
import { EngineCompare } from "../components/EngineCompare";
import { Comments } from "../components/Comments";
import { Cover } from "../components/Cover";
import { VersionCompare } from "../components/VersionCompare";
import { Waveform } from "../components/Waveform";
import { TitleEditor } from "../components/TitleEditor";
import { LyricsSection, SongActions, useSong } from "../components/SongDetail";
import { usePlayer } from "../context/PlayerContext";
import { AuthorLink } from "../components/AuthorLink";
import { ENGINES, genreName, LLMS, STATUS } from "../lib/presets";

const SongArt = ({ song }) => {
  const p = usePlayer();
  const active = p.current?.id === song.id;
  return (
    <div className="space-y-5">
      <Cover song={song} testId="song-cover" className="w-full aspect-square rounded-3xl border border-[var(--c-line)] shadow-2xl" />
      {song.status === "completed" && (
        <Waveform seed={song.id} n={70} className="h-14" testId="song-waveform"
          progress={active && p.duration ? p.time / p.duration : 0} onSeek={(f) => active && p.seek(f * p.duration)} />
      )}
    </div>
  );
};

const SongMeta = ({ song, setSong }) => (
  <>
    <p className="text-xs uppercase tracking-[0.2em] text-[var(--c-orange)] font-semibold">{genreName(song.genre) || "Lietuviška daina"}{song.instrumental && " · Instrumentinė"}</p>
    <TitleEditor song={song} onSaved={setSong} className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold break-words min-w-0" />
    <p className="text-zinc-400 mt-3"><AuthorLink song={song} testId="song-author-link" /> · {new Date(song.created_at).toLocaleDateString("lt-LT")}</p>
    <div className="flex gap-5 mt-3 text-sm text-zinc-500">
      <span className="flex items-center gap-1.5"><Headphones className="w-4 h-4" />{song.plays}</span>
      <span className="flex items-center gap-1.5"><Heart className="w-4 h-4" />{song.likes}</span>
      <span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" />{song.comments || 0}</span>
      <span data-testid="song-engine-badge" className="font-mono text-xs px-2 py-0.5 rounded-md bg-[var(--c-surface)] border border-[var(--c-line)]">
        {song.mode === "cover" ? "ACE-Step remix" : ENGINES.find((e) => e.id === song.engine)?.name || song.engine}
        {song.llm && ["simple", "extend"].includes(song.mode) && !song.instrumental && ` · ${LLMS.find((l) => l.id === song.llm)?.name || song.llm}`}
      </span>
    </div>
  </>
);

export default function SongPage() {
  const { id } = useParams();
  const { song, setSong, missing } = useSong(id);

  if (missing) return <p data-testid="song-not-found" className="text-zinc-400">Daina nerasta. <Link to="/atrasti" className="text-[var(--c-orange)]">Grįžti</Link></p>;
  if (!song) return <div className="h-64 rounded-3xl shimmer" />;
  const done = song.status === "completed";

  return (
    <div className="grid lg:grid-cols-[380px_minmax(0,1fr)] gap-10 xl:gap-16 rise">
      <SongArt song={song} />
      <div className="min-w-0">
        <SongMeta song={song} setSong={setSong} />
        {!done && <p data-testid="song-status" className="mt-6 text-[var(--c-violet)]">{STATUS[song.status]?.label} {song.error}</p>}
        {done && <SongActions song={song} setSong={setSong} />}
        {song.compare_id && <EngineCompare song={song} />}
        {done && <VersionCompare song={song} />}
        {song.style && (
          <div className="mt-10">
            <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">Stilius</p>
            <p data-testid="song-style" className="font-mono text-sm text-zinc-300">{song.style}</p>
          </div>
        )}
        {song.lyrics && <LyricsSection song={song} />}
        {done && <Comments song={song} />}
      </div>
    </div>
  );
}
